/**
 * Cached wrapper for Cognito fetchUserAttributes.
 *
 * Multiple components mount simultaneously after login and all call
 * fetchUserAttributes() — this causes a burst that hits Cognito's
 * TooManyRequestsException rate limit.
 *
 * This module-level cache deduplicates concurrent calls into a single
 * Cognito request per page load. The cache is in-memory only (not
 * localStorage) so it is cleared automatically on tab close or refresh.
 *
 * TTL: 5 minutes — after which the next call fetches fresh attributes.
 * This ensures subscription/company changes are picked up within a session.
 */

import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import type { AuthSession } from 'aws-amplify/auth';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// ── fetchUserAttributes cache ─────────────────────────────────────────────────

let cachedAttributes: Record<string, string | undefined> | null = null;
let attrCacheTimestamp: number | null = null;
let attrInFlightPromise: Promise<Record<string, string | undefined>> | null = null;

/**
 * Fetch user attributes with deduplication and TTL-based caching.
 * Safe to call from multiple components simultaneously.
 */
export async function getCachedUserAttributes(): Promise<Record<string, string | undefined>> {
  const now = Date.now();

  // Return cached value if still fresh
  if (cachedAttributes && attrCacheTimestamp && (now - attrCacheTimestamp) < CACHE_TTL_MS) {
    return cachedAttributes;
  }

  // Deduplicate concurrent calls — return the same in-flight promise
  if (attrInFlightPromise) return attrInFlightPromise;

  attrInFlightPromise = fetchUserAttributes()
    .then((attrs) => {
      cachedAttributes = attrs as Record<string, string | undefined>;
      attrCacheTimestamp = Date.now();
      attrInFlightPromise = null;
      return cachedAttributes;
    })
    .catch((err) => {
      attrInFlightPromise = null;
      throw err;
    });

  return attrInFlightPromise;
}

// ── fetchAuthSession cache ────────────────────────────────────────────────────
// fetchAuthSession is called by 10+ components on mount and is rate-limited
// even more aggressively than fetchUserAttributes.

let cachedSession: AuthSession | null = null;
let sessionCacheTimestamp: number | null = null;
let sessionInFlightPromise: Promise<AuthSession> | null = null;

/**
 * Fetch auth session with deduplication and TTL-based caching.
 * Safe to call from multiple components simultaneously.
 */
export async function getCachedAuthSession(): Promise<AuthSession> {
  const now = Date.now();

  // Return cached value if still fresh (2 min TTL — tokens expire, need fresher)
  if (cachedSession && sessionCacheTimestamp && (now - sessionCacheTimestamp) < 2 * 60 * 1000) {
    return cachedSession;
  }

  if (sessionInFlightPromise) return sessionInFlightPromise;

  sessionInFlightPromise = fetchAuthSession()
    .then((session) => {
      cachedSession = session;
      sessionCacheTimestamp = Date.now();
      sessionInFlightPromise = null;
      return cachedSession;
    })
    .catch((err) => {
      sessionInFlightPromise = null;
      throw err;
    });

  return sessionInFlightPromise;
}

/**
 * Invalidate the cache — call this after updating user attributes
 * or on sign-out so the next call fetches fresh data.
 */
export function invalidateUserAttributesCache(): void {
  cachedAttributes = null;
  attrCacheTimestamp = null;
  attrInFlightPromise = null;
  // Also invalidate session cache on explicit refresh
  cachedSession = null;
  sessionCacheTimestamp = null;
  sessionInFlightPromise = null;
}
